import { ApolloCache, useQuery } from "@apollo/client";
import { CreateAssumptionsMutation } from "~/gql/graphql";
import { graphql } from "~/gql";
import { Assumption } from "~/design-system";
export const MATTER_ASSUMPTIONS_QUERY = graphql(/* GraphQL */ `
  query FindMatterAssumptions($input: Float!) {
    matterAssumptions(input: $input) {
      autoFocus @client
      matterId
      createdAt
      updatedAt
      id
      text
      order
      default
      mandatory
      fromLibraryAssumptionId
    }
  }
`);

export function useMatterAssumptionsQuery(matterId: number) {
  return useQuery(MATTER_ASSUMPTIONS_QUERY, {
    variables: { input: matterId },
    fetchPolicy: "cache-and-network",
  });
}

export function addToMatterAssumptionsCache(
  cache: ApolloCache<any>,
  matterId: number,
  assumptions: CreateAssumptionsMutation["createAssumptions"]
) {
  updateMatterAssumptionsCache(cache, matterId, (matterAssumptions: any[]) => {
    return [...assumptions, ...matterAssumptions];
  });
}

export function replaceInMatterAssumptionsCache(
  cache: ApolloCache<any>,
  matterId: number,
  assumption: Assumption
) {
  const { id } = assumption;
  updateMatterAssumptionsCache(cache, matterId, (matterAssumptions: Assumption[]) => {
    return matterAssumptions.map((a: any) => (id === a.id ? { ...assumption } : a));
  });
}

export function removeFromMatterAssumptionsCache(
  cache: ApolloCache<any>,
  matterId: number,
  assumptionIds: number | number[]
) {
  const assumptionIdsToRemove = Array.isArray(assumptionIds) ? assumptionIds : [assumptionIds];
  updateMatterAssumptionsCache(cache, matterId, (matterAssumptions) => {
    return matterAssumptions.filter((a) => assumptionIdsToRemove.find((id) => a.id !== id));
  });
}

function updateMatterAssumptionsCache(
  cache: ApolloCache<any>,
  matterId: number,
  getNewMatterAssumptions: (matterAssumptions: Assumption[]) => Assumption[]
) {
  try {
    const { matterAssumptions } = cache.readQuery({
      query: MATTER_ASSUMPTIONS_QUERY,
      variables: { input: matterId },
    }) || {
      matterAssumptions: [],
    };

    if (matterAssumptions) {
      const newMatterAssumptions = getNewMatterAssumptions(matterAssumptions);
      cache.writeQuery({
        query: MATTER_ASSUMPTIONS_QUERY,
        variables: { input: matterId },
        data: {
          matterAssumptions: newMatterAssumptions.map((x) => ({
            ...x,
            matterId: matterId,
            autoFocus: false,
            createdAt: new Date(),
            updatedAt: new Date(),
            default: x.default ?? false,
            mandatory: x.mandatory ?? false,
            fromLibraryAssumptionId: x.fromLibraryAssumptionId ?? null,
            __typename: "Assumption" as const,
          })),
        },
      });
    }
  } catch (ex) {
    console.error(ex); // TODO: Handle error
  }
}
